import { ref } from 'vue';
import { C_LOGIN_WORKFLOW } from '_acaSrc/utility/constants';
import { trackLoginPageEvent } from '_acaSrc/utility/LoginPageEvents';

export const LOGIN_PAGE_EVENT_ACTIONS = Object.freeze({
    INITIALIZED: 'initialized',
    NAVIGATION_FAILED: 'navigation_failed',
    RESULT: 'result',
    CONTROL_INTERACTED: 'control_interacted',
    USERNAME_CONTINUE: 'username_continue',
    WORKFLOW_RESOLVED: 'workflow_resolved',
    PASSWORD_STEP_DISPLAYED: 'password_step_displayed',
    PASSWORD_SUBMIT: 'password_submit',
    REMEMBER_USERNAME_CHANGED: 'remember_username_changed',
    SHOW_PASSWORD_TOGGLED: 'show_password_toggled',
    FORGOT_CREDENTIALS_SELECTED: 'forgot_credentials_selected',
    IDP_OPTION_DISPLAYED: 'idp_option_displayed',
    IDP_OPTION_SELECTED: 'idp_option_selected',
    SIGN_IN_ANOTHER_WAY_TOGGLED: 'sign_in_another_way_toggled',
    INSTITUTION_SEARCH_STARTED: 'institution_search_started',
    INSTITUTION_SEARCH_RESULTS: 'institution_search_results',
    INSTITUTION_NO_RESULTS: 'institution_no_results',
    INSTITUTION_RESULT_SELECTED: 'institution_result_selected',
    IDP_HANDOFF_STARTED: 'idp_handoff_started',
    ERROR_DISPLAYED: 'error_displayed'
});

export const LOGIN_PAGE_PHASES = Object.freeze({
    USERNAME: 'username',
    PASSWORD: 'password',
    SSO: 'sso',
    UNKNOWN: 'unknown'
});

export const LOGIN_PAGE_STATUS = Object.freeze({
    CHECKED: 'checked',
    UNCHECKED: 'unchecked',
    VISIBLE: 'visible',
    HIDDEN: 'hidden',
    EXPANDED: 'expanded',
    COLLAPSED: 'collapsed',
    HAS_RESULTS: 'has_results',
    NO_RESULTS: 'no_results',
    VALIDATION_ERROR: 'validation_error',
    FAILURE: 'failure'
});

export const LOGIN_PAGE_SELECTIONS = Object.freeze({
    CONTINUE: 'continue',
    SIGN_IN: 'sign_in',
    HELP_SIGNING_IN: 'help_signing_in',
    EXIT_PRIORITY_LOGIN: 'exit_priority_login',
    FORGOT_CREDENTIALS: 'forgot_credentials',
    FORGOT_USERNAME: 'forgot_username',
    FORGOT_USERNAME_PASSWORD: 'forgot_username_password',
    REMEMBER_USERNAME: 'remember_username',
    SHOW_PASSWORD: 'show_password',
    DEFAULT_BUTTON: 'default_button',
    RECENT_BUTTON: 'recent_button',
    SEARCH_RESULT: 'search_result',
    KEYBOARD_RESULT: 'keyboard_result',
    WORKFLOW: 'workflow',
    PRIMARY_INSTITUTION: 'primary_institution',
    SECONDARY_INSTITUTION: 'secondary_institution'
});

export const LOGIN_PAGE_WORKFLOWS = Object.freeze({
    PASSWORD: 'password',
    IDP_REDIRECT: 'idp_redirect',
    NEXT_STEPS: 'next_steps',
    UNKNOWN: 'unknown'
});

export const LOGIN_PAGE_ERROR_KEYS = Object.freeze({
    CLIENT_USERNAME_REQUIRED: 'client.username.required',
    CLIENT_PASSWORD_REQUIRED: 'client.password.required',
    LOGIN_INVALID_CREDENTIALS: 'login.invalid_credentials',
    MERGE_RULES_ERROR: 'merge.rules_error',
    SSO_MERGED_ERROR: 'sso.merged_error',
    INSTITUTION_SEARCH_FAILED: 'institution_search.failed',
    UNKNOWN: 'unknown'
});

const trackEvent = (action, params = {}) => trackLoginPageEvent(action, params);

const shouldUsePasswordPhase = (state = {}) => state.showPasswordSection;

const shouldUseSsoPhase = (state = {}) =>
    state.showSearchableInstitutionsSection && state.isSsoPriority;

const hasSafeErrorKey = (errorKey) => /^[a-zA-Z0-9._-]+$/.test(errorKey);

const getSafeLoginErrorKey = (errorKey) => {
    if (!errorKey) {
        return LOGIN_PAGE_ERROR_KEYS.UNKNOWN;
    }
    if (!hasSafeErrorKey(errorKey)) {
        return LOGIN_PAGE_ERROR_KEYS.UNKNOWN;
    }
    return errorKey;
};

export function getLoginPagePhase(state = {}) {
    if (shouldUsePasswordPhase(state)) {
        return LOGIN_PAGE_PHASES.PASSWORD;
    }
    if (shouldUseSsoPhase(state)) {
        return LOGIN_PAGE_PHASES.SSO;
    }
    return LOGIN_PAGE_PHASES.USERNAME;
}

export function getWorkflowStatus(workflow) {
    if (workflow === C_LOGIN_WORKFLOW.PROMPT_USER_FOR_PASSWORD) {
        return LOGIN_PAGE_WORKFLOWS.PASSWORD;
    }
    if (workflow === C_LOGIN_WORKFLOW.REDIRECT_TO_SSO) {
        return LOGIN_PAGE_WORKFLOWS.IDP_REDIRECT;
    }
    if (workflow === C_LOGIN_WORKFLOW.SHOW_NEXT_STEPS_FOR_SIGN_IN) {
        return LOGIN_PAGE_WORKFLOWS.NEXT_STEPS;
    }
    return LOGIN_PAGE_WORKFLOWS.UNKNOWN;
}

export function normalizeLoginErrorKey(errorKey, state = {}) {
    if (state.isMergeRulesError) {
        return LOGIN_PAGE_ERROR_KEYS.MERGE_RULES_ERROR;
    }
    if (state.isSsoMergedError) {
        return LOGIN_PAGE_ERROR_KEYS.SSO_MERGED_ERROR;
    }
    if (state.isLoginError) {
        return LOGIN_PAGE_ERROR_KEYS.LOGIN_INVALID_CREDENTIALS;
    }
    return getSafeLoginErrorKey(errorKey);
}

export default function useLoginPageTracking() {
    const hasTrackedInitialized = ref(false);
    const hasTrackedPasswordStepDisplayed = ref(false);
    const hasTrackedInstitutionSearchStarted = ref(false);

    const trackControl = (selectionChoice, phase) => trackEvent(
        LOGIN_PAGE_EVENT_ACTIONS.CONTROL_INTERACTED,
        { phase, selectionChoice }
    );

    const trackErrorDisplayed = (
        errorKey,
        phase,
        status = LOGIN_PAGE_STATUS.FAILURE
    ) => trackEvent(LOGIN_PAGE_EVENT_ACTIONS.ERROR_DISPLAYED, {
        phase,
        status,
        errorKey
    });

    const trackPasswordStepDisplayed = (state = {}) => {
        if (!state.showPasswordSection || hasTrackedPasswordStepDisplayed.value) {
            return;
        }
        hasTrackedPasswordStepDisplayed.value = true;
        trackEvent(LOGIN_PAGE_EVENT_ACTIONS.PASSWORD_STEP_DISPLAYED, {
            phase: LOGIN_PAGE_PHASES.PASSWORD,
            status: LOGIN_PAGE_STATUS.VISIBLE
        });
    };

    const trackInitialized = (state = {}) => {
        if (hasTrackedInitialized.value) {
            return;
        }
        hasTrackedInitialized.value = true;
        trackEvent(LOGIN_PAGE_EVENT_ACTIONS.INITIALIZED, {
            phase: getLoginPagePhase(state)
        });
        trackPasswordStepDisplayed(state);
    };

    const trackLoginErrorDisplayed = (errorKey, state = {}) => trackErrorDisplayed(
        normalizeLoginErrorKey(errorKey, state),
        getLoginPagePhase(state)
    );

    const trackUsernameRequired = () => trackErrorDisplayed(
        LOGIN_PAGE_ERROR_KEYS.CLIENT_USERNAME_REQUIRED,
        LOGIN_PAGE_PHASES.USERNAME,
        LOGIN_PAGE_STATUS.VALIDATION_ERROR
    );

    const trackPasswordRequired = () => trackErrorDisplayed(
        LOGIN_PAGE_ERROR_KEYS.CLIENT_PASSWORD_REQUIRED,
        LOGIN_PAGE_PHASES.PASSWORD,
        LOGIN_PAGE_STATUS.VALIDATION_ERROR
    );

    const trackUsernameContinue = () => {
        trackControl(LOGIN_PAGE_SELECTIONS.CONTINUE, LOGIN_PAGE_PHASES.USERNAME);
        trackEvent(LOGIN_PAGE_EVENT_ACTIONS.USERNAME_CONTINUE, {
            phase: LOGIN_PAGE_PHASES.USERNAME
        });
    };

    const trackPasswordSubmit = () => {
        trackControl(LOGIN_PAGE_SELECTIONS.SIGN_IN, LOGIN_PAGE_PHASES.PASSWORD);
        trackEvent(LOGIN_PAGE_EVENT_ACTIONS.PASSWORD_SUBMIT, {
            phase: LOGIN_PAGE_PHASES.PASSWORD
        });
    };

    const trackWorkflowResolved = (workflow, state = {}) => {
        trackEvent(LOGIN_PAGE_EVENT_ACTIONS.WORKFLOW_RESOLVED, {
            phase: LOGIN_PAGE_PHASES.USERNAME,
            status: getWorkflowStatus(workflow)
        });
        trackPasswordStepDisplayed(state);
    };

    const trackForgotCredentialsSelected = (state = {}) => {
        const phase = getLoginPagePhase(state);
        const selectionPick = state.showPasswordSection
            ? LOGIN_PAGE_SELECTIONS.FORGOT_USERNAME_PASSWORD
            : LOGIN_PAGE_SELECTIONS.FORGOT_USERNAME;
        trackControl(LOGIN_PAGE_SELECTIONS.FORGOT_CREDENTIALS, phase);
        trackEvent(LOGIN_PAGE_EVENT_ACTIONS.FORGOT_CREDENTIALS_SELECTED, {
            phase,
            selectionPick
        });
    };

    const trackShowPasswordToggled = (showPassword) => trackEvent(
        LOGIN_PAGE_EVENT_ACTIONS.SHOW_PASSWORD_TOGGLED,
        {
            phase: LOGIN_PAGE_PHASES.PASSWORD,
            status: showPassword ? LOGIN_PAGE_STATUS.VISIBLE : LOGIN_PAGE_STATUS.HIDDEN
        }
    );

    const trackRememberUsernameChanged = (value) => trackEvent(
        LOGIN_PAGE_EVENT_ACTIONS.REMEMBER_USERNAME_CHANGED,
        {
            phase: LOGIN_PAGE_PHASES.USERNAME,
            status: value ? LOGIN_PAGE_STATUS.CHECKED : LOGIN_PAGE_STATUS.UNCHECKED
        }
    );

    const trackHelpSigningIn = (state = {}) => trackControl(
        LOGIN_PAGE_SELECTIONS.HELP_SIGNING_IN,
        getLoginPagePhase(state)
    );

    const trackExitPriorityLogin = (state = {}) => trackControl(
        LOGIN_PAGE_SELECTIONS.EXIT_PRIORITY_LOGIN,
        getLoginPagePhase(state)
    );

    const trackMergeRulesErrorDisplayed = (state = {}) => trackErrorDisplayed(
        LOGIN_PAGE_ERROR_KEYS.MERGE_RULES_ERROR,
        getLoginPagePhase(state)
    );

    const trackIdpHandoffStarted = (loginPageEvent = {}) => trackEvent(
        LOGIN_PAGE_EVENT_ACTIONS.IDP_HANDOFF_STARTED,
        {
            phase: LOGIN_PAGE_PHASES.SSO,
            selectionChoice: loginPageEvent.selectionChoice,
            selectionPick: loginPageEvent.selectionPick
        }
    );

    const getWorkflowSsoOptionEvent = () => ({
        selectionChoice: LOGIN_PAGE_SELECTIONS.WORKFLOW
    });

    const getDefaultSsoOptionEvent = (providerName) => ({
        selectionChoice: LOGIN_PAGE_SELECTIONS.DEFAULT_BUTTON,
        selectionPick: providerName
    });

    const getMicrosoftSsoOptionEvent = () =>
        getDefaultSsoOptionEvent(LOGIN_PAGE_SELECTIONS.PRIMARY_INSTITUTION);

    const getOpenAthensSsoOptionEvent = () =>
        getDefaultSsoOptionEvent(LOGIN_PAGE_SELECTIONS.SECONDARY_INSTITUTION);

    const getRecentInstitutionEvent = () => ({
        selectionChoice: LOGIN_PAGE_SELECTIONS.RECENT_BUTTON
    });

    const getSearchResultClickEvent = (index) => ({
        selectionChoice: LOGIN_PAGE_SELECTIONS.SEARCH_RESULT,
        count: index + 1
    });

    const getSearchResultKeyboardEvent = (index) => ({
        selectionChoice: LOGIN_PAGE_SELECTIONS.KEYBOARD_RESULT,
        count: index + 1
    });

    const trackSsoEvent = (action, params = {}) => trackEvent(action, {
        phase: LOGIN_PAGE_PHASES.SSO,
        ...params
    });

    const trackDisplayedConvenienceOptions = (shouldShowDefaultConvenienceButtons) => {
        if (shouldShowDefaultConvenienceButtons) {
            trackSsoEvent(
                LOGIN_PAGE_EVENT_ACTIONS.IDP_OPTION_DISPLAYED,
                getMicrosoftSsoOptionEvent()
            );
            trackSsoEvent(
                LOGIN_PAGE_EVENT_ACTIONS.IDP_OPTION_DISPLAYED,
                getOpenAthensSsoOptionEvent()
            );
            return;
        }
        trackSsoEvent(
            LOGIN_PAGE_EVENT_ACTIONS.IDP_OPTION_DISPLAYED,
            getRecentInstitutionEvent()
        );
    };

    const trackInstitutionSearchStarted = () => {
        if (hasTrackedInstitutionSearchStarted.value) {
            return;
        }
        hasTrackedInstitutionSearchStarted.value = true;
        trackSsoEvent(LOGIN_PAGE_EVENT_ACTIONS.INSTITUTION_SEARCH_STARTED);
    };

    const resetInstitutionSearchStarted = () => {
        hasTrackedInstitutionSearchStarted.value = false;
    };

    const trackInstitutionSearchResults = (resultCount) => {
        if (resultCount > 0) {
            trackSsoEvent(LOGIN_PAGE_EVENT_ACTIONS.INSTITUTION_SEARCH_RESULTS, {
                status: LOGIN_PAGE_STATUS.HAS_RESULTS,
                count: resultCount
            });
            return;
        }
        trackSsoEvent(LOGIN_PAGE_EVENT_ACTIONS.INSTITUTION_NO_RESULTS, {
            status: LOGIN_PAGE_STATUS.NO_RESULTS
        });
    };

    const trackInstitutionSearchFailed = () => trackSsoEvent(
        LOGIN_PAGE_EVENT_ACTIONS.NAVIGATION_FAILED,
        {
            status: LOGIN_PAGE_STATUS.FAILURE,
            errorKey: LOGIN_PAGE_ERROR_KEYS.INSTITUTION_SEARCH_FAILED
        }
    );

    const trackInstitutionResultSelected = (loginPageEvent) => trackSsoEvent(
        LOGIN_PAGE_EVENT_ACTIONS.INSTITUTION_RESULT_SELECTED,
        loginPageEvent
    );

    const trackSignInAnotherWayToggled = (isExpanded) => trackSsoEvent(
        LOGIN_PAGE_EVENT_ACTIONS.SIGN_IN_ANOTHER_WAY_TOGGLED,
        {
            status: isExpanded ? LOGIN_PAGE_STATUS.EXPANDED : LOGIN_PAGE_STATUS.COLLAPSED
        }
    );

    const trackSsoOptionSelected = (loginPageEvent = {}) => trackSsoEvent(
        LOGIN_PAGE_EVENT_ACTIONS.IDP_OPTION_SELECTED,
        loginPageEvent
    );

    return {
        getLoginPagePhase,
        getWorkflowStatus,
        normalizeLoginErrorKey,
        trackInitialized,
        trackPasswordStepDisplayed,
        trackLoginErrorDisplayed,
        trackUsernameRequired,
        trackPasswordRequired,
        trackUsernameContinue,
        trackPasswordSubmit,
        trackWorkflowResolved,
        trackForgotCredentialsSelected,
        trackShowPasswordToggled,
        trackRememberUsernameChanged,
        trackHelpSigningIn,
        trackExitPriorityLogin,
        trackMergeRulesErrorDisplayed,
        trackIdpHandoffStarted,
        getWorkflowSsoOptionEvent,
        getMicrosoftSsoOptionEvent,
        getOpenAthensSsoOptionEvent,
        getRecentInstitutionEvent,
        getSearchResultClickEvent,
        getSearchResultKeyboardEvent,
        trackDisplayedConvenienceOptions,
        trackInstitutionSearchStarted,
        resetInstitutionSearchStarted,
        trackInstitutionSearchResults,
        trackInstitutionSearchFailed,
        trackInstitutionResultSelected,
        trackSignInAnotherWayToggled,
        trackSsoOptionSelected
    };
}
